"use client";

import React, { useMemo, useEffect } from "react";
import TagManager from "react-gtm-module";
import { useAppContext } from "@components/Provider";
import Post from "src/constants/Post";
import Sites from "src/constants/Sites";

import MainLayout from "@components/Layout/Layout";
import LayoutWithoutAds from "@components/Layout/LayoutWithoutAds";
import SingleColumnLayout from "@components/Layout/SingleColumnLayout";
import SingleColumnLayoutWithAds from "@components/Layout/SingleColumnLayoutWithAds";
import HomePageLayoutWithAds from "@components/Layout/HomePageLayoutWithAds";
import SingleColumnLayoutWithOutHeaderFooter from "@components/Layout/SingleColumnLayoutWithOutHeaderFooter";
// import { getUTMParams, setCookie, readCookie } from 'helpers/Helper';

const PAGE_TYPES = {
  CATEGORY: "Category",
  PUBLISHER: "Publisher",
  HOME_PAGE: "HomePage",
  SEARCH_PAGE: "AlgoliaSearch",
  ACCOUNT: {
    CREATE_PASSWORD: "Profile",
    LDL_REDUMPTION: "Profile",
    PROFILE: "Profile",
    READING_GOALS: "Profile",
    READING_PREFERENCES: "ReadingPreferences",
    RETAILER_PREFERANCES: "Profile",
    THANK_YOU: "Profile",
    WELCOME: "WelcomePage",
    RESETPASSWORD: "ResetPassword",
  },
  CATEGORY_FUNNEL: "CategoryFunnel",
  AD_ISBN: "NewsletterDealsAd",
  FOLLOW_AUTHOR: "FollowAuthor",
  FEEDBACK_PAGE: "FeedBackPage",
  AUTHOR_PAGE: "AuthorPage",
  LOGIN_PAGE: "LoginPage",
  TAGS_PAGE: "TagsPage",
  NEWSLETTERS_PAGE: "subscribePage",
  NEWSLETTERS2_PAGE: "NewsLetters2",
};

function SelectLayout({ children, pageData }) {
  const {
    data,
    siteId,
    isMobile,
    isTablet,
    subscriberCookie,
    isNonClosableModalChecked,
    router,
    statusCode,
  } = useAppContext();

  /**
   * Initialize Google Tag Manager only after page state is stable.
   *
   * This prevents GA from logging soft 404s where GTM fires before
   * routing, layout, and meta tags are fully resolved (e.g. homepage
   * temporarily rendering an error/404 title during hydration).
   *
   * Conditions:
   * - GTM ID must be available
   * - Page must be in a valid (200) state
   * - Initialization is deferred to the next frame to ensure layout
   *   and metadata are finalized before GA events are sent
   */
  useEffect(() => {
    if (!data?.SiteSettings?.gtm_container_id) return;
    if (statusCode && statusCode !== 200) return;

    // Delay GTM until layout + meta are stable
    const id = data.SiteSettings.gtm_container_id;

    requestAnimationFrame(() => {
      TagManager.initialize({ gtmId: id });
    });
  }, [data?.SiteSettings?.gtm_container_id, statusCode]);

  const SelectedLayout = useMemo(() => {
    const pageType = GetPageType(router.asPath);
    const showAdsOnSite = data?.SiteSettings?.ad_settings?.show_ads ?? true;
    let layout = statusCode !== 200 ? SingleColumnLayout : GetDefaultLayout(pageType) || MainLayout;
    if (siteId === Sites.OPENROADMEDIA) {
      const singleColumnLayoutPages = [
        PAGE_TYPES.HOME_PAGE,
        PAGE_TYPES.NEWSLETTERS_PAGE,
        PAGE_TYPES.NEWSLETTERS2_PAGE,
        PAGE_TYPES.SEARCH_PAGE,
      ];
      if (singleColumnLayoutPages.includes(pageType)) {
        layout = SingleColumnLayout;
      } else if (pageType === PAGE_TYPES.CATEGORY) {
        if (isMobile) {
          layout = SingleColumnLayout;
        } else if (isTablet) {
          layout = showAdsOnSite ? SingleColumnLayoutWithAds : LayoutWithoutAds;
        } else {
          layout = showAdsOnSite ? MainLayout : LayoutWithoutAds;
        }
      } else {
        layout = showAdsOnSite ? layout : LayoutWithoutAds;
      }
    } else {
      const PostTypes = [
        Post.TYPES.PAGE,
        Post.TYPES.DEALS,
        Post.TYPES.LANDING_DEALS,
        Post.TYPES.FUNNEL,
        Post.TYPES.CATEGORY_FUNNEL,
        Post.TYPES.GIVEAWAY_PAGE,
        Post.TYPES.TELL_A_FRIEND_PAGE,
        Post.TYPES.INTEREST_PAGE,
      ];
      if (
        pageData.data?.length > 0 &&
        (pageData.data[0]?.Post || pageData.data[0]?.Index) &&
        (PostTypes.includes(pageData.data[0]?.Post?.post?.post_type) ||
          (siteId === Sites.EARLYBIRDBOOKS &&
            (pageData.data[0]?.Post?.post?.post_type == Post.TYPES.EBB_HOMEPAGE ||
              pageData.data[0]?.Index?.post?.post_type == Post.TYPES.HOMEPAGE ||
              pageData.data[0]?.Index?.post?.post_type == Post.TYPES.EBB_HOMEPAGE)))
      ) {
        layout = SingleColumnLayout;
      }
    }
    return layout;
  }, [router]);

  // Extract post type from page data if available
  const postType =
    pageData?.data?.[0]?.Post?.post?.post_type || pageData?.data?.[0]?.Index?.post?.post_type;

  const isSimilarTitlesPage =
    typeof router?.asPath === "string" && router.asPath.includes("/similar-titles");

  const otherData = {
    siteId,
    isMobile,
    isTablet,
    subscriberCookie,
    isNonClosableModalChecked,
    postType,
    isSimilarTitlesPage,
  };

  return <SelectedLayout {...otherData}>{children}</SelectedLayout>;
}

const GetPageType = (asPath) => {
  if (asPath === "/") {
    return PAGE_TYPES.HOME_PAGE;
  } else if (asPath.startsWith("/publisher") && asPath !== "/publisher") {
    return PAGE_TYPES.PUBLISHER;
  } else if (asPath.startsWith("/category/")) {
    return PAGE_TYPES.CATEGORY;
  } else if (asPath.startsWith("/search-results/")) {
    return PAGE_TYPES.SEARCH_PAGE;
  } else if (asPath.startsWith("/account/createpassword")) {
    return PAGE_TYPES.ACCOUNT.CREATE_PASSWORD;
  } else if (asPath.startsWith("/account/ldlredemption")) {
    return PAGE_TYPES.ACCOUNT.LDL_REDUMPTION;
  } else if (asPath.startsWith("/account/profile")) {
    return PAGE_TYPES.ACCOUNT.PROFILE;
  } else if (asPath.startsWith("/account/readinggoals")) {
    return PAGE_TYPES.ACCOUNT.READING_GOALS;
  } else if (asPath.startsWith("/account/readingpreferences")) {
    return PAGE_TYPES.ACCOUNT.READING_PREFERENCES;
  } else if (asPath.startsWith("/account/retailerpreferences")) {
    return PAGE_TYPES.ACCOUNT.RETAILER_PREFERANCES;
  } else if (asPath.startsWith("/account/thankyou")) {
    return PAGE_TYPES.ACCOUNT.THANK_YOU;
  } else if (asPath.startsWith("/account/welcome")) {
    return PAGE_TYPES.ACCOUNT.WELCOME;
  } else if (asPath.startsWith("/reset-password")) {
    return PAGE_TYPES.ACCOUNT.RESETPASSWORD;
  } else if (asPath.startsWith("/signup/")) {
    return PAGE_TYPES.CATEGORY_FUNNEL;
  } else if (asPath.startsWith("/author/")) {
    return PAGE_TYPES.AUTHOR_PAGE;
  } else if (asPath.startsWith("/ad/")) {
    return PAGE_TYPES.AD_ISBN;
  } else if (asPath.startsWith("/follow/author")) {
    return PAGE_TYPES.FOLLOW_AUTHOR;
  } else if (asPath.startsWith("/feedback")) {
    return PAGE_TYPES.FEEDBACK_PAGE;
  } else if (asPath.startsWith("/login")) {
    return PAGE_TYPES.LOGIN_PAGE;
  } else if (asPath.startsWith("/tags/")) {
    return PAGE_TYPES.TAGS_PAGE;
  } else if (asPath === "/newsletters-2") {
    return PAGE_TYPES.NEWSLETTERS2_PAGE;
  } else if (asPath === "/newsletters") {
    return PAGE_TYPES.NEWSLETTERS_PAGE;
  }

  return asPath;
};

const GetDefaultLayout = (pageType) => {
  let layout = SingleColumnLayout;
  switch (pageType) {
    case PAGE_TYPES.HOME_PAGE:
      layout = HomePageLayoutWithAds;
      break;
    case PAGE_TYPES.AD_ISBN:
      layout = SingleColumnLayoutWithOutHeaderFooter;
      break;
  }
  return layout;
};

export default React.memo(SelectLayout, (prevProps, nextProps) => {
  // Compare children and any other props to avoid re-rendering
  return prevProps.children === nextProps.children;
});
